/**
 * StackReasoning — per-component reasoning for each chassis choice.
 * Uses native <details> / <summary> for zero-JS disclosure.
 */
import { useReveal } from '../lib/anim/useReveal';

type StackItem = {
  name: string;
  category: string;
  reasoning: string;
};

const STACK: StackItem[] = [
  {
    name: 'Cloudflare Pages',
    category: 'Static hosting',
    reasoning:
      'Free static hosting served from 300+ edge POPs. Build once; no cold starts, no scaling config. Every marketing and workspace app deploys here.',
  },
  {
    name: 'Cloudflare Workers',
    category: 'Edge compute',
    reasoning:
      'Same edge network as Pages. V8 isolates start in under 5 ms. Cheaper than containers for the low-to-mid traffic profile of an early-stage SaaS — ideal for platform-api, AI-CTO, and tenant-provisioner.',
  },
  {
    name: 'Workers AI + Vectorize',
    category: 'Embeddings + RAG',
    reasoning:
      'Inference at the edge using the same auth surface as the rest of the stack. bge-base-en-v1.5 at 768 dimensions for embeddings; Vectorize for vector search. No separate Pinecone or Weaviate account needed — the free tier comfortably covers Phase 1 usage.',
  },
  {
    name: 'Workers KV',
    category: 'Session cache',
    reasoning:
      'Eventually-consistent global KV. Used for session tokens and transient state where occasional staleness is acceptable. Fast global reads without running a Redis cluster.',
  },
  {
    name: 'Supabase Sydney (ap-southeast-2)',
    category: 'Postgres + RLS',
    reasoning:
      'Row-Level Security gives the strongest tenant isolation Onwards can buy without managing its own database. Sydney region keeps Australian customers\' data in-country. Postgres means standard SQL, pgvector available, and full control over schema evolution.',
  },
  {
    name: 'Stripe',
    category: 'Billing',
    reasoning:
      'Subscriptions (Pilot / Build / Studio tiers) plus Stripe Connect for customer end-user billing pass-through. Predictable webhook contract and battle-tested SCA/PCI compliance out of the box.',
  },
  {
    name: 'Resend',
    category: 'Transactional email',
    reasoning:
      'One verified domain (onwardsanalytics.com.au) shared across the Onwards portfolio. Simple REST API, no IP-warming theatre, cheap per-email pricing at early volumes.',
  },
  {
    name: 'Anthropic Claude (Haiku 4.5 + Sonnet 4.6)',
    category: 'AI reasoning',
    reasoning:
      'Haiku for high-frequency intake calls (~$0.001/call). Sonnet for AI-CTO reasoning and structured spec generation (~$0.01–0.03/call). Using the best available models requires honest cost reporting — Anthropic tokens are the dominant cost line at scale, not infra.',
  },
  {
    name: 'Audit Trail',
    category: 'Compliance',
    reasoning:
      'Append-only Postgres table with SELECT-only RLS, written exclusively via security-definer functions. One immutable log per tenant. Cheap, queryable, and satisfies "what happened to my data" without leaving the stack.',
  },
  {
    name: 'Magic-link Auth',
    category: 'Authentication',
    reasoning:
      'No passwords to manage. HMAC-SHA256 signed tokens with a 15-minute TTL are issued via email; they exchange for a 30-day session cookie on first click. Low friction for first-time logins and zero password-reset surface area.',
  },
];

function StackItem({ item, delay }: { item: StackItem; delay: 0 | 1 | 2 | 3 | 4 | 5 }) {
  const reveal = useReveal({ delay });

  return (
    <div
      ref={reveal.ref}
      {...reveal.props}
      className={`rounded-xl overflow-hidden${reveal.props.className ? ' ' + reveal.props.className : ''}`}
      style={{ border: '1px solid var(--color-hairline)', background: 'var(--color-surface)' }}
    >
      <details className="group">
        <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none select-none transition-colors" style={{ background: 'var(--color-surface)' }}>
          <div className="flex items-center gap-4 min-w-0">
            <span className="shrink-0 inline-block w-2 h-2 rounded-full" aria-hidden="true" style={{ background: 'var(--color-accent)' }} />
            <span className="text-ink font-semibold text-sm leading-snug">{item.name}</span>
            <span className="hidden sm:inline-block text-xs px-2 py-0.5 rounded-full shrink-0" style={{ color: 'var(--color-mute)', background: 'var(--color-bg)' }}>
              {item.category}
            </span>
          </div>
          {/* Chevron */}
          <svg
            aria-hidden="true"
            className="shrink-0 w-4 h-4 transition-transform duration-200 group-open:rotate-180"
            style={{ color: 'var(--color-mute)' }}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </summary>

        <div className="px-6 pb-6 pt-1">
          <span className="sm:hidden inline-block text-xs px-2 py-0.5 rounded-full mb-3" style={{ color: 'var(--color-mute)', background: 'var(--color-bg)' }}>
            {item.category}
          </span>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--color-mute)' }}>{item.reasoning}</p>
        </div>
      </details>
    </div>
  );
}

export function StackReasoning() {
  const heading = useReveal();

  return (
    <section className="py-20">
      <div className="max-w-4xl mx-auto px-6">
        <div
          ref={heading.ref}
          {...heading.props}
          className={`mb-10 text-center${heading.props.className ? ' ' + heading.props.className : ''}`}
        >
          <p className="text-sm uppercase tracking-widest mb-3 font-medium" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>
            Why each choice
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-ink" style={{ fontFamily: 'var(--font-display)' }}>
            Every component has a reason.
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-sm leading-relaxed" style={{ color: 'var(--color-mute)' }}>
            Onwards doesn't pick tools for prestige. Each one earns its place against three criteria:
            cost at small scale, operational surface area, and data sovereignty.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {STACK.map((item, i) => (
            <StackItem
              key={item.name}
              item={item}
              delay={(i % 3) as 0 | 1 | 2}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
